import React, { useState, useEffect, useMemo } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  ButtonBase,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';

import {
  CheckCircleRounded,
  CancelRounded,
  ArrowBackRounded,
  LoginRounded,
  LogoutRounded,
  RoomRounded,
  RoomOutlined
} from '@mui/icons-material';

import {
  DateTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#16233D',
      light: '#2B3C5C',
      dark: '#0D1526'
    },

    success: {
      main: '#1F8A70'
    },

    error: {
      main: '#B0503A'
    },

    background: {
      default: '#F3F4F7',
      paper: '#FFFFFF'
    },

    text: {
      primary: '#1C2430',
      secondary: '#5B6472'
    },

    divider: '#E4E6EC'
  },

  shape: {
    borderRadius: 10
  },

  typography: {
    fontFamily: '"Inter", "Segoe UI", sans-serif',

    h5: {
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },

    body2: {
      color: '#5B6472'
    }
  },

  components: {
    MuiTextField: {
      defaultProps: {
        size: 'medium'
      },

      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8
          }
        }
      }
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600
        }
      }
    }
  }
});

const monoSx = {
  '& input': {
    fontFamily: '"IBM Plex Mono", "Roboto Mono", monospace',
    letterSpacing: '0.02em'
  }
};

const AttendanceEndorsement = ({ onBack }) => {
  const [formData, setFormData] = useState({
    p_sno: '',
    p_barcode_date: dayjs(),
    p_hstatus: '',
    p_latitude: '',
    p_longitude: '',
    p_reason: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [locationReady, setLocationReady] = useState(false);
  const [locationSource, setLocationSource] = useState(null);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setFormData({
          p_sno: '',
          p_barcode_date: dayjs(),
          p_hstatus: '',
          p_latitude: '',
          p_longitude: '',
          p_reason: ''
        });
        getCurrentLocation();
        setSuccess(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    setLocationReady(false);
    setLocationSource(null);
    setError('');

    if (!navigator.geolocation) {
      setError(
        'Geolocation is not supported by this device or browser.'
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const {
          latitude,
          longitude,
          accuracy
        } = position.coords;

        console.log(
          'Device Latitude:',
          latitude
        );

        console.log(
          'Device Longitude:',
          longitude
        );

        console.log(
          'Location Accuracy:',
          accuracy,
          'meters'
        );

        setFormData((prev) => ({
          ...prev,
          p_latitude: latitude.toString(),
          p_longitude: longitude.toString()
        }));

        setLocationSource('gps');
        setLocationReady(true);
        setError('');
      },

      (geoError) => {
        console.error(
          'Geolocation Error:',
          geoError
        );

        let message =
          'Unable to get device location.';

        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            message =
              'Location permission was denied. Please allow location access and try again.';
            break;

          case geoError.POSITION_UNAVAILABLE:
            message =
              'Device location is currently unavailable. Please check your GPS/location settings.';
            break;

          case geoError.TIMEOUT:
            message =
              'Location request timed out. Please try again.';
            break;

          default:
            message =
              'Unable to get device location. Please try again.';
        }

        setError(message);
        setLocationReady(false);
        setLocationSource(null);

        setFormData((prev) => ({
          ...prev,
          p_latitude: '',
          p_longitude: ''
        }));
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const handleChange = (e) => {
    const {
      name,
      value
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateTimeChange = (newValue) => {
    if (newValue && dayjs(newValue).isAfter(dayjs())) {
      setError('Future dates are not allowed');
      return;
    }
    setError('');
    setFormData((prev) => ({
      ...prev,
      p_barcode_date: newValue
    }));
  };

  const handleStatusSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      p_hstatus: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (formData.p_barcode_date && dayjs(formData.p_barcode_date).isAfter(dayjs())) {
      setError('Cannot select a future date');
      setLoading(false);
      return;
    }

    if (
      !locationReady ||
      !formData.p_latitude ||
      !formData.p_longitude
    ) {
      setError(
        'Device location is required. Please allow location access and try again.'
      );
      setLoading(false);
      return;
    }

    if (
      !formData.p_sno ||
      !formData.p_barcode_date ||
      !formData.p_hstatus
    ) {
      setError(
        'Please fill all required fields (Service No., Date & Time, Status).'
      );
      setLoading(false);
      return;
    }

    const formattedDate =
      dayjs(formData.p_barcode_date)
        .format('MM/DD/YYYY hh:mm:ss A');

    const payload = {
      p_sno: formData.p_sno,
      p_barcode_date: formattedDate,
      p_hstatus: formData.p_hstatus,
      p_latitude: formData.p_latitude,
      p_longitude: formData.p_longitude,
      p_reason: formData.p_reason
    };

    console.log(
      'Submitting Attendance Endorsement:',
      payload
    );

    try {
      await axios.post(
        '/attendance/DGESPostAttEndosment',
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(true);

      setFormData({
        p_sno: '',
        p_barcode_date: dayjs(),
        p_hstatus: '',
        p_latitude: '',
        p_longitude: '',
        p_reason: ''
      });

      getCurrentLocation();

    } catch (err) {
      console.error(
        'Submit Error:',
        err
      );

      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to submit endorsement. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      p_sno: '',
      p_barcode_date: dayjs(),
      p_hstatus: '',
      p_latitude: '',
      p_longitude: '',
      p_reason: ''
    });

    setError('');
    setSuccess(false);

    getCurrentLocation();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (window.history.length > 1) {
      window.history.back();
    }
  };

  const statusOptions = useMemo(
    () => [
      {
        value: 'I',
        label: 'IN',
        hint: 'Clock in for the day',
        icon: LoginRounded,
        tone: 'success'
      },

      {
        value: 'O',
        label: 'OUT',
        hint: 'Clock out for the day',
        icon: LogoutRounded,
        tone: 'error'
      }
    ],
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          py: {
            xs: 3,
            sm: 6
          }
        }}
      >
        <Container maxWidth="sm">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              mb: 2.5
            }}
          >
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: '#EDEFF3'
                  }
                }}
              >
                <ArrowBackRounded
                  fontSize="small"
                />
              </IconButton>
            </Tooltip>

            <Box>
              <Typography
                variant="h5"
                component="h1"
                color="text.primary"
              >
                Attendance Endorsement
              </Typography>
            </Box>
          </Stack>
          <Paper
            elevation={0}
            sx={{
              p: {
                xs: 3,
                sm: 4
              },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3
            }}
          >
            {success && (
              <Alert
                severity="success"
                icon={
                  <CheckCircleRounded
                    fontSize="inherit"
                  />
                }
                sx={{
                  mb: 3,
                  borderRadius: 2
                }}
                onClose={() =>
                  setSuccess(false)
                }
              >
                Endorsement submitted successfully. Form cleared for new entry.
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                icon={
                  <CancelRounded
                    fontSize="inherit"
                  />
                }
                sx={{
                  mb: 3,
                  borderRadius: 2
                }}
                onClose={() =>
                  setError('')
                }
              >
                {error}
              </Alert>
            )}

            <LocalizationProvider
              dateAdapter={AdapterDayjs}
            >
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    required
                    label="Employee service no."
                    name="p_sno"
                    value={formData.p_sno}
                    onChange={handleChange}
                    placeholder="8000000"
                    sx={monoSx}
                  />
                  <DateTimePicker
                    label="Barcode date & time"
                    value={formData.p_barcode_date}
                    onChange={handleDateTimeChange}
                    format="MM/DD/YYYY hh:mm:ss A"
                    maxDateTime={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: monoSx,
                        error: formData.p_barcode_date && dayjs(formData.p_barcode_date).isAfter(dayjs()),
                        helperText: formData.p_barcode_date && dayjs(formData.p_barcode_date).isAfter(dayjs())
                          ? 'Future dates are not allowed'
                          : undefined
                      }
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: 'text.primary'
                      }}
                    >
                      Status
                      <Box
                        component="span"
                        sx={{
                          color: 'error.main'
                        }}
                      >
                        *
                      </Box>
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1.5}
                    >
                      {statusOptions.map(
                        (opt) => {
                          const selected =
                            formData.p_hstatus ===
                            opt.value;

                          const Icon =
                            opt.icon;

                          return (
                            <ButtonBase
                              key={opt.value}
                              onClick={() =>
                                handleStatusSelect(
                                  opt.value
                                )
                              }
                              sx={{
                                flex: 1,
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                border: '1.5px solid',
                                borderColor:
                                  selected
                                    ? `${opt.tone}.main`
                                    : 'divider',
                                bgcolor:
                                  selected
                                    ? (
                                      opt.tone ===
                                        'success'
                                        ? '#EAF6F2'
                                        : '#FBEEE9'
                                    )
                                    : 'transparent',
                                transition:
                                  'border-color 120ms ease, background-color 120ms ease',
                                justifyContent:
                                  'flex-start',
                                gap: 1.25
                              }}
                            >
                              <Icon
                                fontSize="small"
                                sx={{
                                  color:
                                    selected
                                      ? `${opt.tone}.main`
                                      : 'text.secondary'
                                }}
                              />

                              <Box
                                sx={{
                                  textAlign: 'left'
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color:
                                      selected
                                        ? `${opt.tone}.main`
                                        : 'text.primary',
                                    lineHeight: 1.2
                                  }}
                                >
                                  {opt.label}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      'text.secondary'
                                  }}
                                >
                                  {opt.hint}
                                </Typography>
                              </Box>
                            </ButtonBase>
                          );
                        }
                      )}
                    </Stack>
                  </Box>
                  <TextField
                    fullWidth
                    label="Reason"
                    name="p_reason"
                    value={formData.p_reason}
                    onChange={handleChange}
                    placeholder="Optional — e.g. late barcode, forgotten scan"
                    multiline
                    minRows={2}
                  />

                  <Divider />
                  <Stack
                    direction="row"
                    spacing={1.5}
                    justifyContent="flex-end"
                  >
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleClear}
                      disabled={loading}
                    >
                      Clear
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={
                        loading ||
                        !locationReady ||
                        !formData.p_latitude ||
                        !formData.p_longitude
                      }
                      startIcon={
                        loading
                          ? (
                            <CircularProgress
                              size={18}
                              color="inherit"
                            />
                          )
                          : null
                      }
                      sx={{
                        minWidth: 170,
                        py: 1.1,
                        backgroundColor:
                          '#1976D2',
                        '&:hover': {
                          backgroundColor:
                            '#1565C0'
                        }
                      }}
                    >
                      {loading
                        ? 'Submitting…'
                        : 'Submit endorsement'
                      }
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </LocalizationProvider>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AttendanceEndorsement;